/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getViewPortHeight, getViewPortWidth } from "selectors/documentViewSelectors";
import { error, success } from "@react/react-spectrum/Toast";
import { isInitialPageLoaded } from "selectors/pagedViewState";
import { getAJSMetadataStatus, getSecondaryMetaDataStatus, shouldStartRendering } from "selectors/documentSelectors";
import { getViewMode } from "selectors/viewModeSelectors";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { docViewModes, AJSMetadataStatus, SecondaryMetadataStatus } from "constants/enums";
import { allowedPageViewModes, allowedDocViewModes } from "publicApis/constants";
import { isNumber } from "utils/APIUtils";

export const adjustViewPortHeight = (stores, deltaHeight) => {
  let w = getViewPortWidth(stores);
  let h = getViewPortHeight(stores);
  stores.documentViewStore.setViewPortDimensions(w, h + deltaHeight);
};

export const adjustViewPortWidth = (stores, deltaWidth) => {
  let w = getViewPortWidth(stores);
  let h = getViewPortHeight(stores);
  stores.documentViewStore.setViewPortDimensions(w + deltaWidth, h);
};

export const setPageViewMode = (stores, viewMode, zoomLevel) => {
  if (viewMode && !isNumber(zoomLevel)) {
    if (allowedPageViewModes.hasOwnProperty(viewMode)) {
      const newPageViewModifier = allowedPageViewModes[viewMode];
      stores.zoom.setPageViewModifier({ newPageViewModifier });
    } else if (allowedDocViewModes.hasOwnProperty(viewMode)) {
      stores.documentStore.setViewMode(allowedDocViewModes[viewMode]);
    }
  }
};

export const initialPageLoadedPromise = stores =>
  new Promise((resolve, reject) => {
    subscribeToSelector(stores, isInitialPageLoaded, (value, reaction) => {
      if (value) {
        reaction.dispose();
        resolve();
      }
    });
    subscribeToSelector(stores, getViewMode, (value, reaction) => {
      if (value === docViewModes.NON_PDF) {
        reaction.dispose();
        reject(new Error("File is not PDF"));
      }
      if (value === docViewModes.NO_PREVIEW) {
        reaction.dispose();
        reject(new Error("File can not be previewed"));
      }
    });
  });

export const fileNameToastReadyPromise = stores =>
  new Promise((resolve, reject) => {
    subscribeToSelector(stores, isInitialPageLoaded, (value, reaction) => {
      if (value) {
        reaction.dispose();
        resolve();
      }
    });
    subscribeToSelector(stores, getViewMode, (value, reaction) => {
      if (value === docViewModes.NON_PDF) {
        subscribeToSelector(stores, shouldStartRendering, (value, reaction) => {
          if (value) {
            reaction.dispose();
            resolve();
          }
        });
      }
      if (value === docViewModes.NO_PREVIEW) {
        reaction.dispose();
        reject(new Error("File can not be previewed"));
      }
    });
  });

export const ajsMetadataDonePromise = stores =>
  new Promise((resolve, reject) => {
    subscribeToSelector(stores, getAJSMetadataStatus, (value, reaction) => {
      if (value === AJSMetadataStatus.SUCCEEDED) {
        reaction.dispose();
        resolve();
      }
      if (value === AJSMetadataStatus.FAILED) {
        reaction.dispose();
        reject(new Error("getMetadata request failed"));
      }
    });
    subscribeToSelector(stores, getViewMode, (value, reaction) => {
      if (value === docViewModes.NON_PDF) {
        reaction.dispose();
        reject(new Error("File is not PDF"));
      }
      if (value === docViewModes.NO_PREVIEW) {
        reaction.dispose();
        reject(new Error("File can not be previewed"));
      }
    });
  });

export const secondaryMetadataDonePromise = stores =>
  new Promise((resolve, reject) => {
    subscribeToSelector(stores, getSecondaryMetaDataStatus, (value, reaction) => {
      if (value === SecondaryMetadataStatus.SUCCEEDED) {
        reaction.dispose();
        resolve();
      } else {
        reaction.dispose();
        reject(new Error("Secondary Metadata Request Failed"));
      }
    });
  });
